<script setup>
import { computed, ref, watch } from 'vue';
import uniqBy from 'lodash/uniqBy';
import { useRoute, useRouter, RouterLink } from 'vue-router';
import { useRouteBreadcrumbs } from '@bignerve/ui-shared';
import CrossRouterLink from '/app/components/CrossRouterLink.vue';
import { usePlatform, useContextAccount } from '/app/composables/platform';
import { useAuth } from '/app/composables/auth';

const props = defineProps({
    prepend: {
        type: Array,
        default: () => [],
    },
    append: {
        type: Array,
        default: () => [],
    },
    disableAutoCrumbs: {
        type: Boolean,
        default: false,
    },
});

const route = useRoute();
const router = useRouter();
const { auth } = useAuth();
const { isUserPlatform, isProfilePage, isViewingSelf } = usePlatform();
const contextAccount = useContextAccount();

const account = computed(() => contextAccount.value || {});

const { breadcrumbs: items } = useRouteBreadcrumbs({ auth }, { route, router });

const crumbs = computed(() => (props.disableAutoCrumbs ? [] : items.value));

const breadcrumbs = computed(() => [
    ...props.prepend,
    ...uniqBy(crumbs.value, 'title'),
    ...props.append,
]);

const homeRoute = ref({ title: 'Home', name: 'home.challenges' });

watch(
    () => account.value.user,
    () => {
        if (isUserPlatform.value && isProfilePage.value && account.value.user?.id) {
            homeRoute.value = {
                title: isViewingSelf.value ? 'My Home' : 'Home',
                name: 'home.user',
                params: {
                    platformSlug: account.value.user.screen_name,
                },
            };
            return;
        }

        homeRoute.value = { title: 'Home', name: 'home.challenges' };
    },
    { immediate: true },
);
</script>

<template>
    <div class="page__breadcrumb flex items-center justify-between overflow-x-auto sb-dust-800 sb-tight sm:overflow-x-none">
        <slot name="prepend" />

        <ul class="breadcrumbs is-large w-full">
            <li><RouterLink class="breadcrumb-item" :to="homeRoute">{{ homeRoute.title }}</RouterLink></li>
            <li v-for="(item, index) in breadcrumbs" :key="index" :class="{ 'text-xl leading-none font-bold text-nerve sm:truncate': index == breadcrumbs.length - 1 }">
                <CrossRouterLink v-if="index < breadcrumbs.length - 1" class="breadcrumb-item" :to="item.route ? item.route : `/${item.path}`">
                    {{ item.title }}
                </CrossRouterLink>
                <template v-else>{{ item.title }}</template>
            </li>
        </ul>

        <slot name="append" />
    </div>
</template>
